import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { doc, getDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  defaultCustomBookingFields,
  mergeBookingContactFields,
  mergeCustomBookingFields,
  standardBookingFieldLabels,
  type BookingContactFieldSettings,
  type CustomBookingFieldSetting,
  type StandardBookingFieldId,
} from '../../lib/bookingSettings';
import { useTenant } from '../../providers/TenantProvider';
import { SettingsShell } from './SettingsShell';

const standardFieldOrder: StandardBookingFieldId[] = [
  'firstName',
  'address',
  'lastName',
  'city',
  'email',
  'postalCode',
  'phone',
  'notes',
];

export function BookingSettingsPage() {
  const { tenantId } = useTenant();
  const [contactFields, setContactFields] = useState<BookingContactFieldSettings>(() =>
    mergeBookingContactFields(null)
  );
  const [customFields, setCustomFields] = useState<CustomBookingFieldSetting[]>(() =>
    mergeCustomBookingFields(defaultCustomBookingFields)
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let mounted = true;

    async function loadSettings() {
      if (!tenantId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const tenantSnap = await getDoc(doc(db, 'tenants', tenantId));
        const bookingSettings = tenantSnap.data()?.bookingSettings || {};
        if (!mounted) return;
        setContactFields(mergeBookingContactFields(bookingSettings.contactFields));
        setCustomFields(mergeCustomBookingFields(bookingSettings.customFields));
      } catch (error) {
        console.error('Error loading booking settings:', error);
        setMessage({ type: 'error', text: 'Failed to load booking settings.' });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadSettings();
    return () => {
      mounted = false;
    };
  }, [tenantId]);

  const updateContactField = (
    fieldId: StandardBookingFieldId,
    key: 'display' | 'required',
    value: boolean
  ) => {
    setContactFields((current) => {
      const field = current[fieldId];
      const display = key === 'display' ? value : field.display;
      return {
        ...current,
        [fieldId]: {
          display,
          required: display ? (key === 'required' ? value : field.required) : false,
        },
      };
    });
  };

  const updateCustomField = (
    index: number,
    patch: Partial<Pick<CustomBookingFieldSetting, 'label' | 'display' | 'required'>>
  ) => {
    setCustomFields((current) =>
      current.map((field, fieldIndex) => {
        if (fieldIndex !== index) return field;
        const nextLabel = patch.label ?? field.label;
        const nextDisplay = patch.display ?? field.display;
        const display = Boolean(nextDisplay && nextLabel.trim());
        return {
          ...field,
          ...patch,
          label: nextLabel,
          display,
          required: display ? patch.required ?? field.required : false,
        };
      })
    );
  };

  const handleSave = async () => {
    if (!tenantId) {
      setMessage({ type: 'error', text: 'Tenant ID not found.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    try {
      const normalizedCustomFields = mergeCustomBookingFields(customFields);
      await updateDoc(doc(db, 'tenants', tenantId), {
        'bookingSettings.contactFields': contactFields,
        'bookingSettings.customFields': normalizedCustomFields,
        updatedAt: Timestamp.now(),
      });
      setCustomFields(normalizedCustomFields);
      setMessage({ type: 'success', text: 'Booking settings saved.' });
    } catch (error: any) {
      console.error('Error saving booking settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save booking settings.' });
    } finally {
      setSaving(false);
    }
  };

  const renderFieldToggles = (
    display: boolean,
    required: boolean,
    onDisplayChange: (value: boolean) => void,
    onRequiredChange: (value: boolean) => void
  ) => (
    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={display}
            onChange={(event) => onDisplayChange(event.target.checked)}
          />
        }
        label="Display"
      />
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={required}
            disabled={!display}
            onChange={(event) => onRequiredChange(event.target.checked)}
          />
        }
        label="Require"
      />
    </Stack>
  );

  return (
    <SettingsShell title="Booking Settings">
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={4}>
          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 2, fontWeight: 700 }}
            >
              Fields
            </Typography>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              {standardFieldOrder.map((fieldId) => {
                const field = contactFields[fieldId];
                return (
                  <Grid item xs={12} md={6} key={fieldId}>
                    <TextField
                      fullWidth
                      label={standardBookingFieldLabels[fieldId]}
                      value=""
                      disabled={!field.display}
                      required={field.required}
                      multiline={fieldId === 'notes'}
                      rows={fieldId === 'notes' ? 2 : undefined}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ readOnly: true }}
                    />
                    {renderFieldToggles(
                      field.display,
                      field.required,
                      (value) => updateContactField(fieldId, 'display', value),
                      (value) => updateContactField(fieldId, 'required', value)
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 2, fontWeight: 700 }}
            >
              Custom Fields
            </Typography>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              {customFields.map((field, index) => (
                <Grid item xs={12} md={6} key={field.id}>
                  <TextField
                    fullWidth
                    label={`Custom Field #${index + 1}`}
                    value={field.label}
                    placeholder="Label"
                    onChange={(event) => updateCustomField(index, { label: event.target.value })}
                    required={field.required}
                    InputLabelProps={{ shrink: true }}
                  />
                  {renderFieldToggles(
                    field.display,
                    field.required,
                    (value) => updateCustomField(index, { display: value }),
                    (value) => updateCustomField(index, { required: value })
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? undefined : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save Booking Settings'}
            </Button>
          </Box>
        </Stack>
      )}
    </SettingsShell>
  );
}
